import getNorthVariations from './North'
import getAlgorithmVariations from './Algorithm'
import getSlimVariations from './Slim'
import getSkanVariations from './Skan'
import getWireflowVariations from './Wireflow'
import ArticleCollection, { getModifier } from '../catalog/ArticleCollection'
import getHaloVariations from './Halo'
import getMayfairVariations from './Mayfair'
import getPalmaVariations from './Palma'
import { articleAttribute, i18nLabelInterface, variationType } from '../catalog/catalog'
import Variation from '../catalog/Variation'
import { VibiaLightParams } from '../composition/Light'
import { BovType, groupAttribute, groupAttributes } from '../composition/composition'
import getTempoVariations from './Tempo'
import getTubeVariations from './Tube'
import getFlatVariations from './Flat'
import getDamaVariations from './Dama'
import getKnitVariations from './Knit'

const defaultRenderAttributes = {
	scale: [1, 1, 1],
	intensity: 2,
	decay: 2,
	penumbra: 1
}
export interface CanopyInterface {
	id: string
	extraCeiling?: number
	name: string
	modifier: string
	reference: string
	source: string
	size: {
		width: number
		height: number
		depth: number
	},
	compatibleMaterials: string[]
	type: string
	compatibleInstallation: string[]
}

interface OptionInterface {
	value: string
	label: string
	multilangLabel: i18nLabelInterface
	buttonColor?: string
	modifier?: string
	description?: i18nLabelInterface
	ral?: string
	hex?: string
	type?: BovType
	MOQ?: number
	thumbnailImage?: string,
	/** DV-6834: inclusive validity (ISO days) of a temporal option. */
	temporal?: { from: string | null, to: string | null },
	compatible: {
		value: boolean,
		reason?: string
	}
}

interface AttributeOptionsInterface {
	options: OptionInterface[],
	extraOptions?: OptionInterface[],
	/** DV-6834: temporal variations superposed for a user enabled by toggle. */
	temporalOptions?: OptionInterface[],
	base: OptionInterface
}

const emptyAttributeOptions: AttributeOptionsInterface = {
	options: [],
	base: null
}

interface AttributesOptionsInterface {
	color: AttributeOptionsInterface
	installation: AttributeOptionsInterface
	control: AttributeOptionsInterface
	temperature: AttributeOptionsInterface
}
export interface LightVariationsInterface {
	colors: string[],
	colorsV2: AttributeOptionsInterface,
	related: string[],
	controls: string[],
	controlsV2: AttributeOptionsInterface,
	extraColors: string[],
	colorButtons: string[],
	extraColorButtons: string[],
	size: {
		width: number,
		height: number,
		depth: number,
	},
	installations: {
		options: CanopyInterface[],
		standard: CanopyInterface
	},
	installationsV2: AttributeOptionsInterface,
	temperatures: string[],
	temperaturesV2: AttributeOptionsInterface,
	paddingTop?: number,
	origin: string,
	buttonColors?: string[],
	shadowButtonColors?: string[]
	canopy: {
		[key: string]: CanopyInterface
	},
	light: {
		type: string,
		options?: any,
		power: number
	},
	pendantHeight: null | {
		min: number,
		standard: number,
		max: number
	}
}

export interface ElementVariationsInterface {
	colorsV2: AttributeOptionsInterface
	controls: string[]
	controlsV2: AttributeOptionsInterface
	extraColors: string[]
	installationsV2: AttributeOptionsInterface
	temperatures: string[]
	temperaturesV2: AttributeOptionsInterface
}

const defaultEmptyCannopy = {
	id: 'empty',
	name: 'empty',
	modifier: 'none',
	reference: 'none',
	size: { width: 0, height: 0, depth: 0 },
	source: '/models/canopy_surface.glb',
	compatibleMaterials: [],
	compatibleInstallation: []
}

export const commonCanopies = {
	"10": {
		id: 'surface',
		extraCeiling: 0,
		name: 'Surface',
		modifier: '10',
		reference: '10 Surface Canopy',
		source: '/models/canopy_surface.glb',
		size: {
			width: 0.195,
			depth: 0.195,
			height: 0.135
		},
		compatibleMaterials: ['all'],
		compatibleInstallation: ['10 Surface Canopy']
	},
	"31": {
		id: 'micro',
		extraCeiling: 0.1,
		name: 'Built-in Micro',
		modifier: '31',
		reference: '31 Remote Micro',
		source: '/models/canopy_micro.glb',
		size: {
			width: 0.035,
			depth: 0.035,
			height: 0.0008
		},
		compatibleMaterials: ['pladur'],
		type: "canopy",
		compatibleInstallation: ['32 Remote XXS', '31 Remote Micro']
	},
	"32": {
		id: 'xxs',
		extraCeiling: 0.1,
		name: 'Built-in XXS',
		modifier: '32',
		reference: '32 Remote XXS',
		source: '/models/canopy_xxs.glb',
		size: {
			width: 0.07,
			depth: 0.07,
			height: 0.003
		},
		compatibleMaterials: ['all'],
		type: "canopy",
		compatibleInstallation: ['32 Remote XXS', '31 Remote Micro']
	},
}

const getCanopies = (models?: string[]) => {
	let options: LightVariationsInterface
	try {
		options = getLightVariations(models[0])
	} catch { } // Fails if no models specified || model has no known 3D data

	return options?.canopy || commonCanopies
}

export const getCanopy = (installation: string, models?: string[]) => {
	if (!installation) return null;
	let modifier = installation
	// reference could be only modifier
	if (modifier.length > 2) { // Se ha pasado solo el modificador
		modifier = getModifier(modifier)
	}

	let canopies = getCanopies(models)
	return canopies[modifier]
}

export const commonVariaton = {
	origin: 'top-center',
	type: 'light',
	size: { width: 0, height: 0, depth: 0 },
	canopy: commonCanopies,
	controls: [],
	temperatures: [],
	colors: [],
	extraColors: [],
	colorButtons: [],
	extraColorButtons: [],
	installations: {
		options: [commonCanopies['10'], commonCanopies['31'], commonCanopies['32']],
		standard: commonCanopies['10'],
	},
	light: {}
}

const staticInformation = {
	...getNorthVariations(commonCanopies, commonVariaton),
	...getAlgorithmVariations(commonCanopies, commonVariaton),
	...getSlimVariations(commonCanopies, commonVariaton),
	...getSkanVariations(commonCanopies, commonVariaton),
	...getWireflowVariations(commonCanopies, commonVariaton),
	...getHaloVariations(commonCanopies, commonVariaton),
	...getPalmaVariations(commonCanopies, commonVariaton),
	...getMayfairVariations(commonCanopies, commonVariaton),
	...getTempoVariations(commonCanopies, commonVariaton),
	...getTubeVariations(commonCanopies, commonVariaton),
	...getFlatVariations(commonCanopies, commonVariaton),
	...getDamaVariations(commonCanopies, commonVariaton),
	...getKnitVariations(commonCanopies, commonVariaton),
	"0190": {
		...commonVariaton,
		size: {
			width: 0.285,
			height: 0.148,
			depth: 0.285
		},
		paddingTop: -0.35,
		familyName: 'tube',
		related: ['6160', '6165'],
		canopy: {
			...commonCanopies,
			"10": {
				...commonCanopies['10'],
				source: '/models/canopy_skan.glb',
				size: {
					width: 0.16,
					depth: 0.16,
					height: 0.033
				},
			}
		},
		light: {
			type: 'spot',
			power: 199,
			options: { angle: Math.PI / 8 }
		}
	},
	"0192": {
		...commonVariaton,
		size: {
			width: 0.285,
			height: 0.148,
			depth: 0.285
		},
		paddingTop: -0.35,
		familyName: 'tube',
		related: ['6160', '6165'],
		canopy: {
			...commonCanopies,
			"10": {
				...commonCanopies['10'],
				source: '/models/canopy_skan.glb',
				size: {
					width: 0.16,
					depth: 0.16,
					height: 0.033
				},
			}
		},
		light: {
			type: 'spot',
			power: 199,
			options: { angle: Math.PI / 8 }
		}
	},
	"0185": {
		...commonVariaton,
		size: {
			width: 0.285,
			height: 0.148,
			depth: 0.285
		},
		paddingTop: -0.35,
		familyName: 'tube',
		related: ['6160', '6165'],
		canopy: {
			...commonCanopies,
			"10": {
				...commonCanopies['10'],
				source: '/models/canopy_skan.glb',
				size: {
					width: 0.16,
					depth: 0.16,
					height: 0.033
				},
			}
		},
		light: {
			type: 'spot',
			power: 199,
			options: { angle: Math.PI / 8 }
		}
	},
	"0180": {
		...commonVariaton,
		size: {
			width: 0.285,
			height: 0.148,
			depth: 0.285
		},
		paddingTop: -0.35,
		familyName: 'tube',
		related: ['6160', '6165'],
		canopy: {
			...commonCanopies,
			"10": {
				...commonCanopies['10'],
				source: '/models/canopy_skan.glb',
				size: {
					width: 0.16,
					depth: 0.16,
					height: 0.033
				},
			}
		},
		light: {
			type: 'spot',
			power: 199,
			options: { angle: Math.PI / 8 }
		}
	},
}

const getCanopyIdFromReference = (reference) => Object.keys(commonCanopies).find(key => commonCanopies[key].reference == reference)

const lightExists = (code: string): Boolean => !!staticInformation[code]
const clusterExists = (code: string): Boolean => !!commonCanopies[code]

const getNonVariableAttributes = (code: string, articleCollection: ArticleCollection) => {
	if (!code || !articleCollection || !articleCollection.getBaseArticle(code))
		return []
	// throw new Error(`Cannot get Variations for light ${code}, undefined Article Collection`)

	const nonVariableAttributes = []
	const variableAttributes: (variationType | 'type')[] = ['color', 'temperature', 'control', 'installation', 'type', 'pendantHeight']

	// const baseArticle = articleCollection.getBaseArticle(code)

	variableAttributes.forEach(attribute => {
		let searchAttribute = attribute
		if (searchAttribute == 'type')
			searchAttribute = 'installation'
		const variations = articleCollection.getVariationsByType(code, searchAttribute)
		if (!variations?.length)
			nonVariableAttributes.push(attribute)
	})

	return nonVariableAttributes
}

/* TODO -> Devolver sistemas de instalación, según las variaciones, no el hardcodeo */
/* TODO -> Separar obtención de datos de catálogo y datos de renderizado */
const getLightVariations = (code: string, articleCollection?: ArticleCollection): LightVariationsInterface => {
	if (articleCollection) {
		const baseArticle = articleCollection.getBaseArticle(code)

		if (!baseArticle && !lightExists(code)) { // is composing pendant
			// TODO: Esto es para que no fallen los test. Quitar este throw.
			throw new Error(`Cannot get Variations for light ${code}, Article Collection doesn\'t contain this light`)
		}

		const pendantHeight = articleCollection.getVariationsByType(code, 'pendantHeight') || []
		const installationVariations = articleCollection?.getVariationsByType(code, 'installation')

		/* Fill installation with information needed by VibiaLights to render 3D Canopies */
		/* FIXME -> Debería desacoplarse esto de la información de dominio, y unir esta información en el lugar donde se renderizan las lights 3D (está añadiendo información innecesaria a entidades que no la necesitan) */
		const installationOptions = installationVariations.map(variation => {
			const canopyReference = variation.getLabel(true)
			const canopyId = getCanopyIdFromReference(canopyReference)

			return canopyId
				? {
					...commonCanopies[canopyId],
					bov: variation.getBOV()
				}
				: {
					...defaultEmptyCannopy,
					id: canopyReference,
					name: canopyReference,
					modifier: variation.getModifier(true),
					reference: canopyReference,
					bov: variation.getBOV()
				}
		})

		/* FIXME -> Los valores para installation siguen sin ser correctos, revisar datos en ERP y como se parsean */
		const attributesOptions: AttributesOptionsInterface = {
			control: emptyAttributeOptions,
			temperature: emptyAttributeOptions,
			installation: emptyAttributeOptions,
			color: emptyAttributeOptions
		}
		const attributesOptionsKeys: variationType[] = ['control', 'temperature', 'installation', 'color']

		attributesOptionsKeys.forEach(attribute => {
			const variations = articleCollection.getVariationsByType(code, attribute)
			const normalVariations = variations.filter(v => !v.isOptional())
			const optionalVariations = variations.filter(v => v.isOptional())
			const baseVariation = variations.find(variation => variation.isBase())

			const variationToOption = (variation: Variation) => {
				const buttonColor = variation.isType('color') ? variation.getValue() : null
				return {
					value: variation.getLabel(true),
					label: variation.getLabel(false),
					multilangLabel: variation.getDescriptions(),
					...buttonColor ? { buttonColor } : {}
				}
			}
			const options = normalVariations.map(variationToOption)
			const extraOptions = optionalVariations.map(variationToOption)
			const base = baseVariation && variationToOption(baseVariation)
			attributesOptions[attribute] = {
				options,
				...extraOptions?.length ? { extraOptions } : {},
				base
			}
		})
		attributesOptions.temperature.options.sort((a, b) => { return a.label > b.label ? 1 : -1 })

		const table = {
			...commonVariaton,
			...staticInformation[code],
			related: articleCollection?.getBaseArticles()?.map(article => article.getModel()) || [], // Replaces composing pendants related (allows non-composing pendants models depending on article collection)
			installations: {
				options: installationOptions,
				standard: installationOptions.find(option => option.bov == 'B')
			},
			controls: attributesOptions.control.options.map(option => option.value) || [],
			temperatures: attributesOptions.temperature.options.map(option => option.value) || [],
			colors: attributesOptions.color.options.map(option => option.value) || [],
			extraColors: attributesOptions.color.extraOptions?.map(option => option.value) || [],
			colorButtons: attributesOptions.color.options.map(option => option.buttonColor) || [],
			extraColorButtons: attributesOptions.color.extraOptions?.map(option => option.buttonColor) || [],
			pendantHeight: pendantHeight?.length ? pendantHeight[0].getValue() : null,
			controlsV2: attributesOptions.control,
			installationsV2: attributesOptions.installation,
			temperaturesV2: attributesOptions.temperature,
			colorsV2: attributesOptions.color,
		}
		return JSON.parse(JSON.stringify(table))
	} else if (lightExists(code)) {
		return JSON.parse(JSON.stringify(staticInformation[code]))
	} else {
		throw new Error(`Cannot get Variations for light ${code}, undefined Article Collection`)
	}
}

// TODO: Deprecate
const getLightParams = (model: string, articleCollection: ArticleCollection) => {
	const baseArticle = articleCollection.getBaseArticle(model)
	const attributes = baseArticle?.getAttributes(true)
	const lightAttributes: VibiaLightParams = {
		code: model,
		attributes: attributes ? { // TODO: Construct using 'attributeIsPersonalizable(model)' (implement new function)
			...(attributes.color && { color: attributes.color }),
			...(attributes.temperature && { temperature: attributes.temperature }),
			...(attributes.control && { control: attributes.control }),
		} : {},
		renderAttributes: defaultRenderAttributes
	}
	return lightAttributes
}

// TODO: Deprecate
const getClusterParams = (model: string, articleCollection: ArticleCollection) => {
	const baseArticle = articleCollection.getBaseArticle(model)
	const attributes = baseArticle.getAttributes(true)
	const clusterAttributes = {
		installation: attributes.installation
	}
	return clusterAttributes
}

const getBaseValue = (attribute: variationType, options: LightVariationsInterface) => {
	switch (attribute) {
		case 'color':
		case 'control':
		case 'temperature':
		case 'installation':
			return options[`${attribute}sV2`]?.base?.value
		case 'pendantHeight':
			return options.pendantHeight?.standard
		default:
			return null
	}
}

const getInitialLightParams = (model: string, articleCollection: ArticleCollection) => {
	const requiredAttributes = articleCollection.getRequiredAttributes(model).filter(attribute => attribute != 'installation')
	const personalizableAttributes = articleCollection.getPersonalizableAttributes(model)
	const lightOptions = getLightVariations(model, articleCollection)
	const lightParams: VibiaLightParams = {
		code: model,
		attributes: {},
		renderAttributes: defaultRenderAttributes
	}
	for (const attribute of requiredAttributes) {
		if (attribute == 'color' || attribute == 'pendantHeight' || !personalizableAttributes.includes(attribute)) {
			const baseValue = getBaseValue(attribute, lightOptions)
			if (baseValue) lightParams.attributes[attribute] = baseValue
		}
	}
	return lightParams
}

const getInitialClusterParams = (model: string, articleCollection: ArticleCollection) => {
	const requiredAttributes: groupAttribute[] = ['installation']
	const personalizableAttributes = articleCollection.getPersonalizableAttributes(model)
	const lightOptions = getLightVariations(model, articleCollection)
	const clusterAttributes: groupAttributes = {}
	for (const attribute of requiredAttributes) {
		if (!personalizableAttributes.includes(attribute)) {
			const baseValue = getBaseValue(attribute, lightOptions)
			if (baseValue) clusterAttributes[attribute] = baseValue as string
		}
	}
	return clusterAttributes
}

const getDefaultAttributeValue = (model: string, attribute, articleCollection: ArticleCollection): string | number => {
	if (!articleCollection) return null
	if (attribute != 'pendantHeight') {
		const variations = articleCollection.getVariationsByType(model, attribute)
		const baseVariation = variations.find(v => v.isBase())
		return baseVariation?.getLabel(true) ?? null
	} else {
		return articleCollection.getDefaultPendantHeight(model)
	}
}

const isAttributeValid = (model: string, attribute, value: string | number, articleCollection: ArticleCollection) => {
	const variationTypes = Variation.getTypes();
	if (!variationTypes.includes(attribute)) return true

	if (!articleCollection) return true

	if (attribute != 'pendantHeight') {
		const variationModifiers = articleCollection.getVariationsByType(model, attribute).map(v => v.getModifier(true))
		if (variationModifiers?.length > 1 && (value == undefined || value == null)) {
			return true
		}
		const valueModifier = getModifier(value)
		return variationModifiers.includes(valueModifier)
	} else {
		return articleCollection.isPendantHeightValid(model, value)
	}
}

export default {
	getDefaultAttributeValue,
	isAttributeValid,
	getLightVariations,
	getLightParams,
	getClusterParams,
	getInitialLightParams,
	getInitialClusterParams,
	getNonVariableAttributes,
	lightExists: lightExists,
	clusterExists: clusterExists,
	getCanopy,
	getCanopyIdFromReference,
	getAllElements: () => {
		return JSON.parse(JSON.stringify(staticInformation))
	}
}
